import styles from './ExamChangeInfoSharedStyling.module.scss'
import { ExamChangeVisualization } from '@/components/ExamChangeGuidePage/ExamChangeVisualization/ExamChangeVisualization'
import React, { ReactElement } from 'react'
import { ExamChangeInfoSharedProps } from '@/components/ExamChangeGuidePage/ExamChangeInfo/ExamChangeInfoSharedProps.types'
import { reusableCssClass } from '@/utils/reusableCssClasses'

export const Spring2023: React.FC<ExamChangeInfoSharedProps> = (
    _props
): ReactElement => {
    return (
        <div
            className={styles.examChangeGuideSharedComponent}
            id={'spring2023'}
        >
            <div className={styles.examChangeGuidePageTop}>
                <h2>Spring 2023</h2>
                <h3>Summary</h3>
                <div>
                    The SOA has released changes effective for the Spring 2023
                    sitting of FSA Group and Health Exams. The updated syllabi
                    can be found here:
                    <ul>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/education/exam-req/edu-exam-group-health-design-pricing/'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Design and Pricing
                            </a>
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/education/exam-req/edu-exam-group-health-finance-val/'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Valuation and Regulation
                            </a>
                            (formerly Finance and Valuation)
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/education/exam-req/edu-exam-group-health-specialty/'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Risk Mitigation
                            </a>
                            (formerly Specialty)
                        </li>
                    </ul>
                    These changes can be summarized at a high level as follows:
                    <ol>
                        <li>
                            Design and Pricing (DP) exam name remains unchanged.
                            <ul>
                                <li>
                                    Exam length has decreased from 5 to 3.5
                                    hours.
                                </li>
                                <li>
                                    3 out of 5 former DP sections remain. Plan
                                    Provisions now also includes Government
                                    Programs. Employee Benefits and Provider
                                    Reimbursement have been moved to RM.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Valuation and Regulation (VR-US) replaces Finance
                            and Valuation (FV-US).
                            <ul>
                                <li>
                                    Exam length has decreased from 5 to 3.5
                                    hours.
                                </li>
                                <li>
                                    4 out of 6 former FV sections remain.
                                    Government Programs has been moved to DP.
                                    Actuarial Appraisal has been dropped.
                                </li>
                            </ul>
                        </li>
                        <li>
                            Risk Mitigation (RM) replaces Specialty (SPC).
                            <ul>
                                <li>
                                    Exam length has increased from 2 to 3 hours.
                                </li>
                                <li>
                                    2 out of 4 former SPC sections remain.
                                    Employee Benefits and Provider Reimbursement
                                    from former DP have been added. ERM has been
                                    dropped.
                                </li>
                            </ul>
                        </li>
                        <li>
                            The total exam time of Group and Health has been
                            reduced from 12 to 10 hours.
                        </li>
                    </ol>
                </div>
            </div>
            <h3>Mapping</h3>
            <p>
                The below shows a mapping of syllabus items from the old exam
                onto the new exam:
            </p>
            <ul>
                <li>
                    &quot;NEW&quot; denotes syllabus items on the Spring 2023
                    exams that were not previously covered by the Group & Health
                    exams.
                </li>
                <li>
                    &quot;REMOVED&quot; denotes syllabus items on the former
                    Group & Health exams that are no longer covered in the new
                    exams.
                </li>
                <li>
                    Use the checkboxes on the right to filter by source type.
                </li>
            </ul>
            <ExamChangeVisualization />
            <h3>Recommendations and Observations</h3>
            <ol>
                <li>
                    <b>
                        If you have recently passed the old GHDP, we recommend
                        you take GHRM in May 2023.
                    </b>{' '}
                    The new GHRM is comprised of half old GHDP material that you
                    (hopefully) already know and the exam will count towards a
                    different exam requirement.
                </li>
                <li>
                    <b>
                        The overall amount of content covered by the new Group &
                        Health Exams is less than the old Group & Health Exams.
                    </b>{' '}
                    Previously, there were 175 syllabus items. Now there are 137
                    syllabus items. Similarly, the total exam time is shorter
                    (12 to 10 hours).
                </li>
                <li>
                    One textbook was removed (old GHSPC,{' '}
                    <i>Financial Enterprise Risk Management</i>). One textbook
                    was added (new GHDP, <i>Insuring Long-Term Care</i>). The
                    majority of syllabus item changes are across Study Notes,
                    Websites, and ASOPs.
                </li>
                <li>
                    There are new syllabus items in GHDP: Manual Rates, Plan
                    Provisions / Government Programs.
                </li>
                <li>
                    There are new syllabus items in GHRM: Claims / Disease
                    Management.
                </li>
                <li>
                    There are new syllabus items in GHVR-US: Financial
                    Statements, Regulation / Taxation.
                </li>
            </ol>
        </div>
    )
}
